import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Wallet, BookOpen, Clock, Video, Sparkles, LogOut, Key, ClipboardList, Target, AlertTriangle, ArrowRight, Loader2, Zap, Star, Home } from 'lucide-react';
import api from '../../api/axios';
import StatCard from '../../components/StatCard';
import StatusBadge from '../../components/StatusBadge';
import DepositModal from '../../components/DepositModal';
import { useTranslation } from 'react-i18next';

const StudentHome: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [isDepositOpen, setIsDepositOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '' });

  const [exams, setExams] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any[]>([]);
  const [recentSessions, setRecentSessions] = useState<any[]>([]);
  
  const [studentStats, setStudentStats] = useState({ lessonsCompleted: 0, totalStudyHours: 0 });
  const [loading, setLoading] = useState(true);

  const [ratingSessionId, setRatingSessionId] = useState<string | null>(null);
  const [ratingVal, setRatingVal] = useState(0);
  const [ratingComment, setRatingComment] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = 'auto';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [examsRes, analyticsRes, sessionsRes, statsRes] = await Promise.all([
          api.get('/exams'),
          api.get('/exams/analytics'),
          api.get('/sessions/student/recent'),
          api.get('/sessions/student/stats')
        ]);
        setExams(examsRes.data);
        setAnalytics(analyticsRes.data);
        setRecentSessions(sessionsRes.data);
        if (statsRes.data) {
          setStudentStats(statsRes.data);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return setPasswordStatus({ type: 'error', message: t('passwords_do_not_match', 'New passwords do not match') });
    }

    try {
      await api.post('/auth/change-password', {
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordStatus({ type: 'success', message: t('password_updated_terminating', 'Password updated! Terminating session...') });
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });

      setTimeout(() => {
        logout();
        navigate('/login?reason=password_changed');
      }, 2000);

    } catch (err: any) {
      setPasswordStatus({ type: 'error', message: err.response?.data?.message || t('password_auth_failed', 'Password change authorization failed') });
    }
  };

  const submitRetroactiveRating = async () => {
    if (!ratingSessionId || ratingVal === 0) return;
    try {
      await api.post('/sessions/review', { sessionId: ratingSessionId, rating: ratingVal, comment: ratingComment });
      setRecentSessions(prev => prev.map(s => s.id === ratingSessionId ? { ...s, review: { rating: ratingVal } } : s));
      setRatingSessionId(null);
      setRatingVal(0);
      setRatingComment('');
    } catch (err) {
      console.error("Failed to submit rating", err);
    }
  };

  const weakTopics = Array.from(new Set(
    analytics.flatMap(a => a.weakTopics || [])
  ));

  return (
    <div className="w-full min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-slate-900 via-slate-800 to-indigo-950 text-white p-4 md:p-8 pb-32 relative">
    <header className="h-16 md:h-20 shrink-0 flex items-center justify-between px-3 md:px-8 border-b border-slate-800 bg-slate-950/80 backdrop-blur-xl z-50 rounded-2xl mb-6">
        <div className="flex items-center gap-2 md:gap-10 overflow-hidden">
          <Link to="/student/home" className="flex items-center gap-2 cursor-pointer shrink-0">
            <div className="w-8 h-8 md:w-12 h-12 bg-indigo-600 rounded-xl md:rounded-2xl flex items-center justify-center font-black italic text-lg md:text-2xl text-white shadow-lg shadow-indigo-600/20 group-active:scale-90 transition-all">A</div>
            <div className="hidden xs:block">
              <h1 className="text-[10px] md:text-sm font-black uppercase tracking-tighter text-white">{t('app_name', 'AtronsTutor')}</h1>
              <p className="text-[7px] md:text-[9px] text-indigo-500 font-bold uppercase tracking-widest leading-none">{t('console', 'Console')}</p>
            </div>
          </Link>

          <nav className="flex gap-1 overflow-x-auto no-scrollbar py-1">
            <Link to="/student/home" className="whitespace-nowrap px-3 py-1.5 rounded-lg text-[9px] font-black uppercase text-white bg-indigo-600 shadow-lg shadow-indigo-600/20">{t('dash', 'Dash')}</Link>
            <Link to="/student/my-jobs" className="whitespace-nowrap px-3 py-1.5 rounded-lg text-[9px] font-black uppercase text-slate-400 bg-slate-800 hover:text-white transition-all">{t('my_class', 'My Class')}</Link>
            <Link to="/student/post-job" className="whitespace-nowrap px-3 py-1.5 rounded-lg text-[9px] font-black uppercase text-emerald-400 bg-emerald-500/10 border border-emerald-500/20">+ {t('new_request', 'New Request')}</Link>
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 bg-slate-900 px-3 py-1.5 rounded-xl border border-slate-800 transition-all shadow-xl">
            <div className="flex flex-col items-start leading-none">
              <span className="text-[10px] font-black text-white">{Number(user?.walletBalance || 0).toFixed(0)} <span className="text-[6px] text-slate-500 uppercase">ETB</span></span>
            </div>
          </button>
          <button onClick={() => window.confirm(t('sign_out_prompt', "Sign out?")) && logout()} className="p-2.5 md:p-3 text-slate-500 bg-slate-800 border border-slate-700 rounded-xl md:rounded-2xl hover:text-red-500 hover:bg-red-500/10 transition-all flex items-center justify-center shadow-lg"><LogOut size={18} /></button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-slate-950 p-6 rounded-3xl border border-slate-800 shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-indigo-600/10 text-indigo-400 rounded-2xl flex items-center justify-center border border-indigo-500/20"><Sparkles size={24} /></div>
          <div>
            <h1 className="text-xl font-black uppercase tracking-tight">{t('hello', 'Hello')}, {user?.fullName || t('student', 'Student')}</h1>
            <p className="text-xs text-slate-400 font-medium">{t('student_greeting_subtitle', 'Access your interactive workspace or request immediate homework assistance.')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-center bg-slate-900 px-4 py-2 rounded-2xl border border-slate-800">
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">{t('account_status', 'Account Status:')}</span>
          <StatusBadge active={true} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatCard label={t('available_balance', 'Available Balance')} value={`${Number(user?.walletBalance || 0).toFixed(0)} ETB`} icon={<Wallet size={20} className="text-indigo-400" />} colorClass="bg-indigo-500/10 text-indigo-400 border border-indigo-500/20" />
        <StatCard label={t('lessons_completed', 'Lessons Completed')} value={`${studentStats.lessonsCompleted} ${t('sessions', 'Sessions')}`} icon={<BookOpen size={20} className="text-emerald-400" />} colorClass="bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" />
        <StatCard label={t('total_study_time', 'Total Study Time')} value={`${Number(studentStats.totalStudyHours).toFixed(1)} hrs`} icon={<Clock size={20} className="text-blue-400" />} colorClass="bg-blue-500/10 text-blue-400 border border-blue-500/20" />
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-slate-950 p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex flex-col min-h-[300px]">
          <h2 className="text-sm font-black uppercase tracking-widest text-indigo-400 mb-4 flex items-center gap-2">
            <Clock size={16} /> {t('recent_class_history', 'Recent Class History')}
          </h2>

          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-3">
            {loading ? (
              <div className="text-center text-slate-500 font-bold uppercase tracking-widest mt-10 animate-pulse">{t('loading_history', 'Loading History...')}</div>
            ) : recentSessions.length === 0 ? (
              <div className="text-center p-6 border border-slate-800 border-dashed rounded-2xl text-slate-500 font-bold text-xs uppercase">{t('no_completed_classes', 'No completed classes yet.')}</div>
            ) : (
              recentSessions.map(session => (
                <div key={session.id} className="bg-slate-900 border border-slate-800 p-4 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <span className="text-[9px] font-black uppercase text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded-md mb-1 inline-block">{session.job?.subject || t('tutoring', 'Tutoring')}</span>
                    <h3 className="text-sm font-black text-white">{session.tutor?.fullName || t('instructor', 'Instructor')}</h3>
                    <p className="text-[10px] text-slate-500 font-bold">{new Date(session.endTime).toLocaleDateString()}</p>
                  </div>

                  {session.review ? (
                    <div className="flex items-center gap-1 bg-amber-500/10 px-3 py-1.5 rounded-lg border border-amber-500/20">
                      <Star size={14} className="fill-amber-400 text-amber-400" />
                      <span className="text-[10px] font-black text-amber-400">{session.review.rating}.0 {t('rated', 'Rated')}</span>
                    </div>
                  ) : ratingSessionId === session.id ? (
                    <div className="flex flex-col gap-2 w-full sm:w-auto bg-slate-950 p-2 rounded-lg border border-slate-700">
                      <div className="flex gap-1 justify-center">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star key={star} size={20} onClick={() => setRatingVal(star)} className={`cursor-pointer ${star <= ratingVal ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />
                        ))}
                      </div>
                      <button onClick={submitRetroactiveRating} disabled={ratingVal === 0} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white text-[9px] font-black uppercase py-1.5 rounded disabled:opacity-50">{t('submit_rating', 'Submit Rating')}</button>
                    </div>
                  ) : (
                    <button onClick={() => setRatingSessionId(session.id)} className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-white border border-slate-700 hover:border-slate-500 px-4 py-2 rounded-xl transition-all">
                      {t('rate_tutor', 'Rate Tutor')}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="bg-slate-950 p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex flex-col flex-1">
            <div>
              <h2 className="text-sm font-black uppercase tracking-widest text-emerald-400 mb-2">{t('need_help', 'Need Help?')}</h2>
              <p className="text-xs text-slate-400 leading-relaxed font-medium">
                {t('need_help_desc', 'Instantly broadcast a homework question or setup an immediate lesson with an online tutor.')}
              </p>
            </div>
            <Link to="/student/post-job" className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white text-center font-black uppercase tracking-wider text-[10px] rounded-xl shadow-xl block mt-auto transition-all active:scale-95">
              + {t('post_new_request', 'Post New Lesson Request')}
            </Link>
          </div>

          <div className="bg-slate-950 p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex flex-col relative overflow-hidden">
            <h2 className="text-sm font-black uppercase tracking-widest text-blue-400 mb-2 flex items-center gap-1.5">
              <Wallet size={16} /> {t('top_up_wallet', 'Top Up Wallet')}
            </h2>
            <p className="text-[10px] text-slate-400 leading-relaxed font-bold mb-6">
              {t('top_up_desc_1', 'Add funds to your account via')} <span className="text-white">Telebirr</span> {t('or', 'or')} <span className="text-white">CBE</span> {t('top_up_desc_2', 'to instantly book tutoring sessions and take exams.')}
            </p>

            <button 
              onClick={() => setIsDepositOpen(true)}
              className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-widest text-[10px] rounded-xl shadow-xl transition-all active:scale-95 flex items-center justify-center gap-2 mt-auto"
            >
              {t('deposit_funds', 'Deposit Funds')} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-slate-950 p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex flex-col min-h-[300px]">
          <h2 className="text-sm font-black uppercase tracking-widest text-indigo-400 mb-4 flex items-center gap-2">
            <ClipboardList size={16} /> {t('mock_exams_practice', 'Mock Exams & Practice')}
          </h2>

          {loading ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 text-xs font-bold uppercase tracking-widest animate-pulse">{t('loading_exams', 'Loading Exams...')}</div>
          ) : exams.length === 0 ? (
            <div className="flex-1 flex flex-col justify-center items-center p-6 border border-slate-800 bg-slate-900/50 rounded-2xl border-dashed">
              <span className="text-2xl mb-2">📚</span>
              <p className="text-xs text-slate-400 uppercase font-black tracking-widest">{t('no_published_exams', 'No published exams yet.')}</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {exams.map(exam => (
                <div key={exam.id} className="bg-slate-900 border border-slate-800 p-4 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:border-indigo-500/50 transition-colors">
                  <div>
                    <h3 className="font-black text-sm uppercase tracking-tight text-slate-200">{exam.title}</h3>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-[10px] font-bold text-slate-500 bg-slate-800 px-2 py-0.5 rounded-lg">{exam.subject}</span>
                      <span className="text-[10px] font-bold text-slate-500 bg-slate-800 px-2 py-0.5 rounded-lg">{exam.durationMinutes} {t('mins', 'Mins')}</span>
                      <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-lg border border-emerald-500/20">
                        {exam.price > 0 ? `${exam.price} ETB` : t('free', 'FREE')}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => navigate(`/student/exam/${exam.id}`)} className="px-6 py-2.5 bg-indigo-600 text-white hover:bg-indigo-500 rounded-xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg">
                    {t('take_exam', 'Take Exam')} <ArrowRight size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <div className="bg-slate-950 p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex flex-col h-full">
            <h2 className="text-sm font-black uppercase tracking-widest text-emerald-400 mb-4 flex items-center gap-2">
              <Target size={16} /> {t('performance_insights', 'Performance Insights')}
            </h2>

            {analytics.length === 0 ? (
              <p className="text-xs text-slate-500 font-medium leading-relaxed">{t('take_exam_desc', 'Take a mock exam to generate your personalized learning analytics.')}</p>
            ) : (
              <div className="space-y-4 flex-1">
                {analytics.slice(0, 3).map((attempt, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-slate-900 p-3 rounded-xl border border-slate-800">
                    <div className="truncate pr-2">
                      <p className="text-[10px] font-black uppercase text-slate-300 truncate">{attempt.exam.title}</p>
                      <p className="text-[8px] text-slate-500 font-bold uppercase">{new Date(attempt.startTime).toLocaleDateString()}</p>
                    </div>
                    <div className={`px-2 py-1 rounded-lg border text-[10px] font-black ${attempt.score >= 50 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                      {Number(attempt.score).toFixed(0)}%
                    </div>
                  </div>
                ))}
              </div>
            )}

            {weakTopics.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-800">
                <div className="flex items-start gap-2 mb-3">
                  <AlertTriangle size={14} className="text-amber-400 mt-0.5" />
                  <p className="text-[10px] font-bold text-amber-400/90 leading-tight">
                    {t('system_analysis_detected', 'System analysis detected struggles with:')} <span className="text-white font-black">{weakTopics.slice(0, 2).join(", ")}</span>
                  </p>
                </div>
                <Link to="/student/post-job" className="w-full py-3 bg-amber-500 text-slate-900 hover:bg-amber-400 text-center font-black uppercase tracking-wider text-[10px] rounded-xl shadow-xl block transition-all active:scale-95">
                  {t('book_tutor_review', 'Book Tutor for Review')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto lg:max-w-7xl lg:mx-0">
        <div className="bg-slate-950 p-6 rounded-[2rem] border border-slate-800 shadow-2xl flex flex-col">
          <h2 className="text-sm font-black uppercase tracking-widest text-indigo-400 mb-4 flex items-center gap-2">
            <Key size={16} /> {t('account_security', 'Account Security')}
          </h2>

          {passwordStatus.message && (
            <div className={`p-3 rounded-xl text-[10px] font-black uppercase border mb-3 ${passwordStatus.type === 'success'
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                : 'bg-red-500/10 text-red-400 border-red-500/20'
              }`}>
              {passwordStatus.message}
            </div>
          )}

          <form onSubmit={handlePasswordChange} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-wider block mb-1">{t('current_password', 'Current Password')}</label>
              <input type="password" value={passwordData.oldPassword} onChange={e => setPasswordData({ ...passwordData, oldPassword: e.target.value })} className="w-full bg-slate-900 border border-slate-800 px-3 py-3 text-xs text-white rounded-xl outline-none focus:border-indigo-500 transition-colors" required />
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-wider block mb-1">{t('new_password', 'New Password')}</label>
              <input type="password" value={passwordData.newPassword} onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })} className="w-full bg-slate-900 border border-slate-800 px-3 py-3 text-xs text-white rounded-xl outline-none focus:border-indigo-500 transition-colors" required />
            </div>
            <div>
              <label className="text-[9px] font-black text-slate-500 uppercase tracking-wider block mb-1">{t('confirm_new_password', 'Confirm New Password')}</label>
              <div className="flex gap-2">
                <input type="password" value={passwordData.confirmPassword} onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })} className="flex-1 bg-slate-900 border border-slate-800 px-3 py-3 text-xs text-white rounded-xl outline-none focus:border-indigo-500 transition-colors" required />
                <button type="submit" className="px-6 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-black uppercase tracking-wider text-[10px] transition-all active:scale-95 shadow-lg">
                  {t('update', 'Update')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <button onClick={() => navigate('/student/home')} className="fixed bottom-6 right-6 bg-indigo-600 hover:bg-indigo-500 text-white p-4 rounded-full shadow-2xl shadow-indigo-500/30 transition-all hover:-translate-y-1 z-50 group flex items-center gap-0 overflow-hidden">
        <Home size={24} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-3 transition-all duration-300 ease-in-out whitespace-nowrap font-bold text-sm uppercase tracking-wider">{t('dashboard', 'Dashboard')}</span>
      </button>

      <DepositModal isOpen={isDepositOpen} onClose={() => setIsDepositOpen(false)} onSuccess={() => window.location.reload()} />
    </div>
  );
};

export default StudentHome;